import React from 'react';
import { motion } from 'motion/react';
import { CASE_STUDIES } from '@/src/constants';
import { ArrowRight, Target, Zap, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CaseStudies() {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-10">
          <div>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-8 tracking-tighter">
              Proven <span className="text-accent">Results</span>
            </h2>
            <p className="text-2xl text-muted-foreground max-w-3xl font-light leading-relaxed">
              Real-world examples of how we've secured complex business environments and mitigated critical risks.
            </p>
          </div>
          <Link to="/services" className="flex items-center gap-3 text-accent font-bold text-lg group whitespace-nowrap pb-2">
            Explore All Services <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-2" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {CASE_STUDIES.map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card rounded-[2.5rem] overflow-hidden border-white/5 flex flex-col"
            >
              <div className="h-64 relative overflow-hidden">
                <img 
                  src={study.image} 
                  alt={study.title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute top-6 left-6">
                  <span className="px-4 py-1.5 rounded-full bg-accent/90 text-background text-[10px] font-bold uppercase tracking-widest backdrop-blur-md">
                    {study.industry}
                  </span>
                </div>
              </div>
              
              <div className="p-8 md:p-10 flex-1 flex flex-col">
                <h3 className="text-2xl font-bold mb-8 leading-tight">{study.title}</h3>
                
                <div className="space-y-8 flex-1">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
                      <Target className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">The Problem</h4>
                      <p className="text-sm text-white/80 leading-relaxed">{study.problem}</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center shrink-0">
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Our Solution</h4>
                      <p className="text-sm text-white/80 leading-relaxed">{study.solution}</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-green-500/10 text-green-500 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">The Result</h4>
                      <p className="text-sm text-white/80 leading-relaxed font-medium">{study.result}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-white/5">
                  <button className="flex items-center gap-2 text-accent font-bold text-sm group">
                    Read Full Case Study <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
